import React from "react"
import { useState, useEffect, useContext, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Alert, Button, Card, ListGroup, ListGroupItem, Stack, Form, Dropdown } from "react-bootstrap"
import { UserContext } from "../context/UserContext";
import CameraArrow from "./CameraArrow";
import { useFetchData } from "../helpers/FetchDataHelpers";
import { useCameraImage, CAMERA_IMAGE_KINDS, IMAGE_STATUS } from "../helpers/useCameraImage";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { FaBarcode } from "react-icons/fa6";
import { RiResetLeftLine } from "react-icons/ri";
import { FcInspection } from "react-icons/fc";
import { MdAddAPhoto } from "react-icons/md";


export default function CameraDetails(props) {

    const BLUR = 10; // Blur value for the snapshot image

    const actions = React.useMemo(() => ({
        SCAN: 'SCAN',
        RESET: 'RESET',
        QA: 'QA',
        SNAPSHOT: 'SNAPSHOT',
        NO_ACTION: 'NO_ACTION'
    }), []);

    const { cameraCircle, deploymentId, refetchCameras } = props;
    const [cameraInfo, setCameraInfo] = useState(null);
    const [ongoingAction, setOngoingAction] = useState(actions.NO_ACTION);
    const { userenv } = useContext(UserContext);
    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const [nautobotJobId, setNautobotJobId] = useState(null);
    const [nautobotJobTimeElapsed, setNautobotJobTimeElapsed] = useState(0);

    const cameraId = cameraCircle.camera.camera_ids[0];
    const fetchData = useFetchData();

    const previewImage = useCameraImage(deploymentId, cameraId, CAMERA_IMAGE_KINDS.PREVIEW);
    const deploymentImage = useCameraImage(deploymentId, cameraId, CAMERA_IMAGE_KINDS.DEPLOYMENT);

    const fetchCameraInfo = useCallback(async () => {
        const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`
        const url = baseUrl + `/cameras/${cameraId}`;
        const { isResponseOK, data, errorMessage } = await fetchData(url, "GET");
        if (isResponseOK && data) {
            console.log("Camera data fetched:", data);
            setCameraInfo(data);
        }
        else {
            toast.error("Error: " + errorMessage);
        }
    }, [userenv, deploymentId, cameraId, fetchData]);

    useEffect(() => {
        fetchCameraInfo();
    }, [deploymentId, cameraId, userenv, fetchCameraInfo])


    useEffect(() => {
        const interval = setInterval(async () => {
            if (!nautobotJobId) {
                return;
            }
            const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
            const url = baseUrl + `/cameras/${cameraId}/ip-address?nautobot_job_id=${nautobotJobId}`;
            const { isResponseOK, data, errorMessage } = await fetchData(url, "PATCH", false);
            if (isResponseOK && data) {
                if (data.job_status === "SUCCESS") {
                    setNautobotJobId(null);
                    setNautobotJobTimeElapsed(0);
                    toast.success("Network configuration job completed!");
                    fetchCameraInfo();
                    refetchCameras(deploymentId);
                    setOngoingAction(actions.NO_ACTION);
                }
                setNautobotJobTimeElapsed(nautobotJobTimeElapsed + 5);
            }
            else {
                toast.error("Error: " + errorMessage);
                setNautobotJobId(null);
                setNautobotJobTimeElapsed(0);
                setOngoingAction(actions.NO_ACTION);
            }
        }, 5000);
        return () => {
            clearInterval(interval);
        };
    }, [nautobotJobId, deploymentId, cameraId, userenv, cameraInfo, nautobotJobTimeElapsed, fetchData, fetchCameraInfo, actions])

    const onSubmitScanCamera = (formData) => {
        const fetchScanCamera = async () => {
            setButtonsDisabled(true);
            const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
            const scanUrl = `/cameras/${cameraId}/scan?scan_value=${formData.scanValue}`;
            const { isResponseOK, data, errorMessage } = await fetchData(baseUrl + scanUrl, "PATCH");
            if (isResponseOK && data) {
                let newCameraInfo = cameraInfo;
                newCameraInfo.scan = formData.scanValue;
                newCameraInfo.mac_address = data.mac_address;
                newCameraInfo.backend_id = data.onboard_id;
                setCameraInfo(newCameraInfo);
                setNautobotJobId(data.nautobot_job_id);
                setOngoingAction(actions.NO_ACTION);
                refetchCameras(deploymentId);
            }
            else {
                toast.error("Error: " + errorMessage);
            }
            setButtonsDisabled(false);
        };
        fetchScanCamera();
    }

    const onSubmitQACheckCamera = (qaValue) => {
        const submitQACheckCamera = async () => {
            const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
            const scanUrl = `/cameras/${cameraId}/qa-check?qa_check_value=${qaValue}`;
            const { isResponseOK, errorMessage } = await fetchData(baseUrl + scanUrl, "PATCH");
            if (isResponseOK) {
                fetchCameraInfo();
                refetchCameras(deploymentId);
            }
            else {
                toast.error("Error: " + errorMessage);
            }
            setButtonsDisabled(false);
        }
        submitQACheckCamera();
    }

    const onClickResetCamera = () => {
        const fetchResetCamera = async () => {
            setButtonsDisabled(true);
            const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
            const resetUrl = `/cameras/${cameraId}/reset`;
            const { isResponseOK, data, errorMessage } = await fetchData(baseUrl + resetUrl, "PATCH");
            if (isResponseOK && data) {
                let newCameraInfo = cameraInfo;
                newCameraInfo.mac_address = "";
                newCameraInfo.backend_id = -1;
                setCameraInfo(newCameraInfo);
                toast.success("Reset successful!");
                setOngoingAction(actions.NO_ACTION);
                refetchCameras(deploymentId);
            }
            else {
                toast.error("Error: " + errorMessage);
            }
            setButtonsDisabled(false);
        };
        fetchResetCamera();
    }

    const onClickCaptureSnapshotCamera = () => {
        const captureSnapshot = async () => {
            setButtonsDisabled(true);
            const baseUrl = `${import.meta.env.VITE_STORE_DEPLOYMENT_API_URL}/store-deployments/${userenv}/${deploymentId}`;
            const snapshotUrl = `/cameras/${cameraId}/deployment-image?blur=${BLUR}`;
            const { isResponseOK, errorMessage } = await fetchData(baseUrl + snapshotUrl, "POST");
            if (isResponseOK) {
                toast.success("Snapshot successful!");
                deploymentImage.refetch();
                setOngoingAction(actions.NO_ACTION);
            }
            else {
                toast.error("Error: " + errorMessage);
            }
            setButtonsDisabled(false);
        };
        captureSnapshot();
    }

    if (!cameraInfo) {
        return (
            <Card className="App-labels mb-5 ">
                <Card.Body className="Card-body">
                    <Card.Title className="mb-3">Camera Data</Card.Title>
                    Fetching camera details...
                </Card.Body>
            </Card>
        )
    }

    return (
        <Stack gap={3}>
            <Stack direction="horizontal" gap={3} className="justify-content-between align-items-center">
                <h5 className="mb-0">Camera {cameraInfo.backend_id}</h5>
                <Dropdown>
                    <Dropdown.Toggle variant="secondary" size="sm" disabled={buttonsDisabled}>
                        Actions
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => { setOngoingAction(actions.SCAN); }}>
                            <FaBarcode className="me-2" />
                            Scan
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => { setOngoingAction(actions.RESET); }}>
                            <RiResetLeftLine className="me-2" />
                            Reset
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => { setOngoingAction(actions.SNAPSHOT); }}>
                            <MdAddAPhoto className="me-2" />
                            Snapshot
                        </Dropdown.Item>
                        
                        <Dropdown.Item onClick={() => { setOngoingAction(actions.QA); }}>
                            <FcInspection className="me-2" />
                            Check
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Stack>

            {previewImage.status === IMAGE_STATUS.LOADING && <Alert>Fetching 3D preview...</Alert>}
            {previewImage.status === IMAGE_STATUS.ERROR && <Alert variant="danger">Could not fetch 3D preview!</Alert>}
            {previewImage.status === IMAGE_STATUS.READY && <img src={previewImage.url} alt="Camera Preview" width="100%" />}

            {deploymentImage.status === IMAGE_STATUS.LOADING && <Alert>Fetching camera snapshot...</Alert>}
            {deploymentImage.status === IMAGE_STATUS.ERROR && <Alert variant="danger">Could not fetch camera snapshot!</Alert>}
            {deploymentImage.status === IMAGE_STATUS.READY && <img src={deploymentImage.url} alt="Camera Deployment Snapshot" width="100%" />}

            {ongoingAction === actions.SCAN &&
                <Form noValidate onSubmit={handleSubmit(onSubmitScanCamera)}>
                    <Form.Group controlId="form.ScanValue">
                        <Stack gap={1} direction="horizontal" >
                            <Form.Control type="text" placeholder="<scan value>" autoFocus
                                {...register("scanValue", { required: true })}
                                isInvalid={errors.scanValue} />
                            <Button disabled={buttonsDisabled} variant="danger" size="sm" onClick={() => { reset(); setOngoingAction(actions.NO_ACTION); }}>
                                Cancel
                            </Button>
                            <Button disabled={buttonsDisabled} type="submit" size="sm" variant="success">
                                Scan
                            </Button>
                        </Stack>
                    </Form.Group>
                </Form>
            }

            {ongoingAction === actions.RESET &&
                <Stack gap={3} direction="horizontal" >
                    <span>
                        Reset Camera ?
                    </span>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="danger" size="sm" onClick={() => { setOngoingAction(actions.NO_ACTION); }}>
                        Cancel
                    </Button>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="success" size="sm" onClick={() => { onClickResetCamera(); setOngoingAction(actions.NO_ACTION); }}>
                        Confirm
                    </Button>
                </Stack>
            }

            {ongoingAction === actions.SNAPSHOT &&
                <Stack gap={3} direction="horizontal" >
                    <span>
                        Capture new snapshot?
                    </span>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="danger" size="sm" onClick={() => { setOngoingAction(actions.NO_ACTION); }}>
                        No
                    </Button>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="success" size="sm" onClick={() => { onClickCaptureSnapshotCamera(); setOngoingAction(actions.NO_ACTION); }}>
                        Yes
                    </Button>
                </Stack>
            }

            {ongoingAction === actions.QA &&
                <Stack gap={3} direction="horizontal" >
                    <span>
                        Camera placement ok?
                    </span>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="danger" size="sm" onClick={() => { onSubmitQACheckCamera(false); setOngoingAction(actions.NO_ACTION); }}>
                        No
                    </Button>
                    <Button disabled={buttonsDisabled} className="flex-grow-1" variant="success" size="sm" onClick={() => { onSubmitQACheckCamera(true); setOngoingAction(actions.NO_ACTION); }}>
                        Yes
                    </Button>
                </Stack>
            }

            <ListGroup>
                <ListGroupItem> Synthetic Id: {cameraInfo.synthetic_id} </ListGroupItem>
                <ListGroupItem> Position (pixels): {cameraCircle.camera.pos_x}, {cameraCircle.camera.pos_y} </ListGroupItem>
                <ListGroupItem> Lens: {cameraInfo.lens} </ListGroupItem>
                <ListGroupItem> Roles: {cameraInfo.roles} </ListGroupItem>
                <ListGroupItem> Height: {cameraInfo.height} </ListGroupItem>
                {cameraInfo.calib_height && <ListGroupItem> Calib. Height: {cameraInfo.calib_height}</ListGroupItem>}
                <ListGroupItem> Pitch: {cameraInfo.pitch}&deg; </ListGroupItem>
                {!cameraInfo.roles.includes("top_view") && <ListGroupItem>
                    <Stack direction="horizontal" gap={3}><div>Yaw: {cameraInfo.yaw}&deg;</div>
                        <CameraArrow yaw={cameraInfo.yaw} /></Stack>
                </ListGroupItem>}
            </ListGroup>

            {nautobotJobId == null && cameraInfo.mac_address !== "" &&
                <ListGroup>
                    <ListGroupItem> Mac Address: {cameraInfo.mac_address} </ListGroupItem>
                    {cameraInfo.ip_address !== "" &&
                        <ListGroupItem> IP Address: {cameraInfo.ip_address} </ListGroupItem>}
                    {cameraInfo.ip_address !== "" &&
                        <ListGroupItem> Stream: <a href={"rtsp://" + cameraInfo.ip_address + "/stream1"}>rtsp://{cameraInfo.ip_address}/stream1</a></ListGroupItem>}
                    {cameraInfo.mac_address !== "" && <ListGroupItem> Backend Id: {cameraInfo.backend_id} </ListGroupItem>}
                    <ListGroupItem>
                        Connected: &nbsp;
                        <Form.Check inline disabled checked={cameraInfo.connection_check} type="checkbox" id="connection-check-switch" />
                    </ListGroupItem>
                    <ListGroupItem> QA Check: &nbsp;
                        <Form.Check inline disabled checked={cameraInfo.qa_check} type="checkbox" id="qa-check-switch" />
                    </ListGroupItem>
                </ListGroup>
            }

            {nautobotJobId &&
                <>
                    <Alert className="mt-3" variant="warning">
                        Waiting for network configuration job ({nautobotJobId}) to complete...
                        <br />
                        Time elapsed: {nautobotJobTimeElapsed} seconds
                    </Alert>
                    <Alert variant="danger">
                        Warning: Do not plug the camera network cable and don&apos;t close this window until the job is completed!
                    </Alert>
                </>
            }
        </Stack>
    )
}
